import type { LocalizedText, Service, ServiceKind, Slot } from '@360face/domain';

/**
 * Kiosk v2 presentation rules (no prices or business rules here — those come from the server).
 *
 * 1. Service variants (Booksy "Service Variants"): one card per treatment, the length inside it —
 *    "Face Workout · 35 / 45 / 60 / 65 min" instead of four rows. Groups are by Hub service id
 *    (the same ids in mock and Altegio mode); a group with fewer than two services present
 *    falls back to plain cards.
 * 2. Extras: at most three suggestions that suit the chosen treatment (Baymard: one irrelevant
 *    suggestion devalues all of them), the full list one tap away. Nothing is pre-selected (EU CRD).
 */
export interface VariantGroupDef {
  id: string;
  title: LocalizedText;
  serviceIds: string[];
}

export const VARIANT_GROUPS: VariantGroupDef[] = [
  {
    id: 'face-workout',
    title: {
      pt: 'Face Workout',
      en: 'Face Workout',
      ru: 'Face Workout — тренировка лица',
      uk: 'Face Workout — тренування обличчя',
    },
    serviceIds: ['express30', 'optimal45', 'max60', 'maxplus65'],
  },
  {
    id: 'body-massage',
    title: {
      pt: 'Massagem de corpo',
      en: 'Body massage',
      fr: 'Massage du corps',
      es: 'Masaje corporal',
      ru: 'Массаж тела',
      uk: 'Масаж тіла',
      de: 'Körpermassage',
    },
    serviceIds: ['body60', 'body90', 'body120'],
  },
  {
    id: 'anticellulite',
    title: {
      pt: 'Massagem anticelulite',
      en: 'Anti-cellulite massage',
      ru: 'Антицеллюлитный массаж',
      uk: 'Антицелюлітний масаж',
    },
    serviceIds: ['slim35', 'slim45'],
  },
  {
    id: 'duo',
    title: {
      pt: 'DUO 1+1 (por pessoa)',
      en: 'DUO 1+1 (per person)',
      ru: 'DUO 1+1 (за человека)',
      uk: 'DUO 1+1 (за особу)',
    },
    serviceIds: ['duo45', 'duo60'],
  },
];

export interface ServiceCard {
  /** Group id, or the service id for a single treatment. */
  id: string;
  kind: ServiceKind;
  /** Group title; undefined = use the only variant's own title. */
  title?: LocalizedText;
  /** Shortest first. */
  variants: Service[];
}

/** Bookable treatments (no add-ons) as cards, in catalog order, variants grouped. */
export function serviceCards(services: Service[], groups: VariantGroupDef[] = VARIANT_GROUPS): ServiceCard[] {
  const main = services.filter((s) => s.kind !== 'addon' && s.active);
  const groupOf = new Map<string, VariantGroupDef>();
  for (const g of groups) {
    const present = g.serviceIds.filter((id) => main.some((s) => s.id === id));
    if (present.length >= 2) for (const id of present) groupOf.set(id, g);
  }
  const cards: ServiceCard[] = [];
  const done = new Set<string>();
  for (const s of main) {
    const g = groupOf.get(s.id);
    if (!g) {
      cards.push({ id: s.id, kind: s.kind, variants: [s] });
      continue;
    }
    if (done.has(g.id)) continue;
    done.add(g.id);
    const variants = main.filter((x) => groupOf.get(x.id) === g).sort((a, b) => a.durationMin - b.durationMin);
    cards.push({ id: g.id, kind: s.kind, title: g.title, variants });
  }
  return cards;
}

/** Add-ons that suit each kind of treatment, most fitting first (owner can reorder). */
export const EXTRAS_FOR: Record<Exclude<ServiceKind, 'addon'>, string[]> = {
  face: ['addon-buccal', 'addon-micro', 'addon-led', 'addon-alginate'],
  combo: ['addon-buccal', 'addon-led', 'addon-alginate', 'addon-headm'],
  skin: ['addon-led', 'addon-alginate', 'addon-micro', 'addon-mask'],
  body: ['addon-headm', 'addon-hand', 'addon-mask'],
};

/**
 * Up to `max` suggested extras for the treatment: the curated ones first, then any other
 * extra of this therapist. Extras that do not fit before the therapist's next booking are left out.
 */
export function suggestedExtras(addons: Service[], main: Service, freeMinutes: number | null, max = 3): Service[] {
  const fits = (a: Service) => freeMinutes === null || a.durationMin <= freeMinutes;
  const kind = main.kind === 'addon' ? 'face' : main.kind;
  const order = EXTRAS_FOR[kind];
  const ranked = [
    ...order.map((id) => addons.find((a) => a.id === id)).filter((a): a is Service => Boolean(a)),
    ...addons.filter((a) => !order.includes(a.id)),
  ];
  return ranked.filter(fits).slice(0, max);
}

/** Add-ons this therapist performs (empty staff list = everyone). */
export function addonsFor(services: Service[], staffId: string): Service[] {
  return services.filter(
    (s) => s.kind === 'addon' && s.active && (s.staffIds.length === 0 || s.staffIds.includes(staffId)),
  );
}

export type DayPart = 'morning' | 'afternoon' | 'evening';

/** Morning before 12:00, afternoon before 17:00, evening after — local time of the slot. */
export function dayPart(start: string): DayPart {
  const h = Number(start.slice(11, 13));
  return h < 12 ? 'morning' : h < 17 ? 'afternoon' : 'evening';
}

export function slotsByPart(slots: Slot[]): Array<[DayPart, Slot[]]> {
  const parts: DayPart[] = ['morning', 'afternoon', 'evening'];
  return parts
    .map((p) => [p, slots.filter((s) => dayPart(s.start) === p)] as [DayPart, Slot[]])
    .filter(([, list]) => list.length > 0);
}

/**
 * "Any therapist": one button per time (the first therapist free then), so the customer
 * chooses a time, not a name. With a therapist chosen: only her times.
 */
export function visibleSlots(slots: Slot[], staffId: string | undefined): Slot[] {
  if (staffId) return slots.filter((s) => s.staffId === staffId);
  const seen = new Set<string>();
  return slots.filter((s) => (seen.has(s.start) ? false : (seen.add(s.start), true)));
}

/**
 * Owner 17.10.2026: who has free time on each date. Input: free dates per therapist;
 * output: date → therapist ids in the order given.
 */
export function staffByDay(rows: Array<{ id: string; ds: string[] }>): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  for (const { id, ds } of rows) for (const d of ds) (out[d] ??= []).push(id);
  return out;
}

/** Short label under a date: first names, "Alyona · Olena"; from three on "Alyona +2". */
export function dayStaffLabel(names: string[]): string {
  const first = names.map((n) => n.trim().split(/\s+/)[0] ?? '').filter(Boolean);
  if (first.length <= 2) return first.join(' · ');
  return `${first[0]} +${first.length - 1}`;
}
